import {
    DataTag,
    QueryKey,
    Updater,
    useQueryClient
} from "@tanstack/react-query";

export const useSetOrInvalidate = () => {
    const queryClient = useQueryClient();

    function setOrInvalidate<
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
        TQueryFnData = unknown,
        TTaggedQueryKey extends QueryKey = QueryKey,
        TInferredQueryFnData = TTaggedQueryKey extends DataTag<
            unknown,
            infer TaggedValue
        >
            ? TaggedValue
            : TQueryFnData
    >(
        queryKey: TTaggedQueryKey,
        updater: Updater<
            NoInfer<TInferredQueryFnData>,
            NoInfer<TInferredQueryFnData>
        >
    ) {
        let setFailed = false;

        const wrappedUpdater: Updater<
            NoInfer<TInferredQueryFnData> | undefined,
            NoInfer<TInferredQueryFnData> | undefined
        > = old => {
            if (typeof updater === "function") {
                if (old === undefined) {
                    // if old data is undefined, return undefined. (when using updater functions, it is likely that old data is required to work properly)
                    // this probably only will happen if a query has started but not yet finished
                    setFailed = true;
                    return undefined;
                } else {
                    return (
                        updater as unknown as (
                            input: NoInfer<TInferredQueryFnData>
                        ) => NoInfer<TInferredQueryFnData>
                    )(old);
                }
            } else {
                return updater;
            }
        };

        const invalidateKeys: QueryKey[] = [];

        const queries = queryClient.getQueryCache().findAll({ queryKey });
        queries.forEach(query => {
            setFailed = false;
            queryClient.setQueryData(query.queryKey, wrappedUpdater);

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (setFailed || query.state.fetchStatus === "fetching") {
                invalidateKeys.push(query.queryKey);
            }
        });

        // invalidate queries that failed to set or that were midway through fetching
        invalidateKeys.forEach(queryKey => {
            void queryClient.invalidateQueries({ queryKey, exact: true });
        });
    }

    return setOrInvalidate;
};

export const useInvalidateOrRemove = () => {
    const queryClient = useQueryClient();

    const invalidateOrRemove = (queryKey: QueryKey): Promise<unknown> => {
        const promises: Promise<void>[] = [];

        const queries = queryClient.getQueryCache().findAll({ queryKey });
        queries.forEach(query => {
            if (query.observers.length === 0) {
                // if there are no longer any observers, remove the query so that Suspense boundaries will be retriggered if data is requested again
                // (invalidating the query will not trigger Suspense boundaries and will briefly show old data
                //   (this is react-query's default SWR (stale while revalidate) behaviour, but we prefer to revert to a loader when we think the underlying data has changed))
                queryClient.removeQueries({
                    queryKey: query.queryKey,
                    exact: true
                });
            } else {
                // query is currently in use, so invalidation is used to trigger a refetch
                // we await the result so that things like form submission loader spinners will show until new data is available
                promises.push(
                    queryClient.invalidateQueries({
                        queryKey: query.queryKey,
                        exact: true
                    })
                );
            }
        });

        if (promises.length > 0) {
            return Promise.all(promises);
        } else {
            return Promise.resolve();
        }
    };

    return invalidateOrRemove;
};
